// src/Utils/SahiSpeech.js

class SahiSpeechRecognition {
    constructor() {
        this.isNativeApp = !!(window.webkit?.messageHandlers?.sahiSpeech || window.AndroidSpeech);
        this.activeRecognition = null;
        this.activeTargetId = null;
    }

    isSupported() {
        // Check if any speech input method is available
        return this.isNativeApp || 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window;
    }

    start(targetElementId, onResult, onError, langCode = 'en-IN') { // ✅ Added langCode param
        this.onResultCallback = onResult;
        this.onErrorCallback = onError;
        this.activeTargetId = targetElementId;

        // Map your language names to BCP-47 codes
        const langMap = {
            'English': 'en-IN',
            'Hindi': 'hi-IN',
            'Marathi': 'mr-IN',
            'Gujarati': 'gu-IN',
            'Tamil': 'ta-IN',
            'Telugu': 'te-IN',
            'Kannada': 'kn-IN',
            'Bengali': 'bn-IN',
            'Punjabi': 'pa-IN'
        };

        const selectedLang = langMap[langCode] || langCode || 'en-IN';

        if (window.webkit?.messageHandlers?.sahiSpeech) {
            window.webkit.messageHandlers.sahiSpeech.postMessage({
                action: 'start',
                targetElementId,
                lang: selectedLang
            });
            return;
        }

        // 2) Android native app
        if (window.AndroidSpeech?.start) {
            window.AndroidSpeech.start(targetElementId, selectedLang);
            return;
        }

        // 3) Web browser fallback (Chrome/Edge)
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

        if (SpeechRecognition) {
            this.activeRecognition = new SpeechRecognition();
            this.activeRecognition.lang = selectedLang;;
            this.activeRecognition.continuous = false;
            this.activeRecognition.interimResults = false;

            this.activeRecognition.onresult = (event) => {
                const transcript = event.results[0][0].transcript;
                if (onResult) {
                    onResult(transcript);
                }
            };

            this.activeRecognition.onerror = (event) => {
                if (onError) {
                    onError(event.error);
                }
                this.activeRecognition = null;
            };

            this.activeRecognition.onend = () => {
                this.activeRecognition = null;
            };

            this.activeRecognition.start();
        } else {
            if (onError) {
                onError('Voice input not supported on this device.');
            }
        }
    }

    stop() {
        // iOS native
        if (window.webkit?.messageHandlers?.sahiSpeech) {
            window.webkit.messageHandlers.sahiSpeech.postMessage({
                action: 'stop'
            });
            return;
        }

        // Android native
        if (window.AndroidSpeech?.stop) {
            window.AndroidSpeech.stop();
            return;
        }

        // Web browser
        if (this.activeRecognition) {
            this.activeRecognition.stop();
            this.activeRecognition = null;
        }
    }

    // Called from native code when STT finishes
    onNativeResult(targetElementId, text) {
        if (this.onResultCallback && targetElementId === this.activeTargetId) {
            this.onResultCallback(text);
        }
    }

    // Called from native code on error
    onNativeError(error) {
        if (this.onErrorCallback) {
            this.onErrorCallback(error);
        }
    }
}

// Create singleton instance
const SahiSpeech = new SahiSpeechRecognition();

// Expose to window for native callbacks
window.SahiSpeech = SahiSpeech;

export default SahiSpeech;